import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, MessageSquare, CheckCircle, Clock, AlertCircle, Trash2, FileText, Mail, Send, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminFeedback() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
      fetchFeedback();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchFeedback = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/feedback`, {
        withCredentials: true
      });
      setFeedback(response.data);
    } catch (error) {
      toast.error('Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  };

  const updateFeedbackStatus = async (feedbackId, status, adminNotes = '') => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/feedback/${feedbackId}`,
        { status, admin_notes: adminNotes },
        { withCredentials: true }
      );
      toast.success('Feedback status updated');
      fetchFeedback();
      setSelectedFeedback(null);
    } catch (error) {
      toast.error('Failed to update feedback');
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!confirm('Are you sure you want to delete this feedback?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/feedback/${feedbackId}`, {
        withCredentials: true
      });
      toast.success('Feedback deleted');
      fetchFeedback();
      setSelectedFeedback(null);
    } catch (error) {
      toast.error('Failed to delete feedback');
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    
    if (!replyText.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/feedback/${selectedFeedback.feedback_id}/reply`,
        { reply_text: replyText },
        { withCredentials: true }
      );
      toast.success('Reply sent! Email notification sent to user.');
      setReplyText('');
      fetchFeedback();
      // Refresh selected feedback to show new reply
      const response = await axios.get(`${BACKEND_URL}/api/admin/feedback`, {
        withCredentials: true
      });
      const updated = response.data.find(fb => fb.feedback_id === selectedFeedback.feedback_id);
      if (updated) {
        setSelectedFeedback(updated);
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const filteredFeedback = filterStatus === 'all' 
    ? feedback 
    : feedback.filter(fb => fb.status === filterStatus);

  const getCategoryBadge = (category) => {
    const badges = {
      question: { color: 'bg-blue-100 text-blue-700', label: 'Question' },
      bug: { color: 'bg-red-100 text-red-700', label: 'Bug Report' },
      feedback: { color: 'bg-green-100 text-green-700', label: 'Feedback' },
      account: { color: 'bg-purple-100 text-purple-700', label: 'Account Issue' },
      payment: { color: 'bg-orange-100 text-orange-700', label: 'Payment' },
      other: { color: 'bg-slate-100 text-slate-700', label: 'Other' }
    };
    
    const badge = badges[category] || badges.other;
    return <span className={`text-xs px-2 py-1 rounded-full ${badge.color}`}>{badge.label}</span>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-orange-500" />;
      case 'reviewed':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-slate-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2">
                <Building2 className="h-6 w-6 text-orange-500" />
                <h1 className="text-xl font-bold text-slate-900">Feedback Management</h1>
              </div>
            </div>
            <span className="text-sm text-slate-600">Hi, {user?.name}</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <MessageSquare className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Feedback</p>
                <p className="text-2xl font-bold text-slate-900">{feedback.length}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Pending</p>
                <p className="text-2xl font-bold text-slate-900">
                  {feedback.filter(fb => fb.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Reviewed</p>
                <p className="text-2xl font-bold text-slate-900">
                  {feedback.filter(fb => fb.status === 'reviewed').length}
                </p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Resolved</p>
                <p className="text-2xl font-bold text-slate-900">
                  {feedback.filter(fb => fb.status === 'resolved').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex gap-2 border-b border-slate-200">
          {['all', 'pending', 'reviewed', 'resolved'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                filterStatus === status
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Feedback List */}
          <div className="space-y-4">
            {filteredFeedback.length === 0 ? (
              <div className="card text-center py-12">
                <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No feedback to display</p>
              </div>
            ) : (
              filteredFeedback.map((fb) => (
                <div
                  key={fb.feedback_id}
                  onClick={() => setSelectedFeedback(fb)}
                  className={`card cursor-pointer transition-all hover:shadow-md ${
                    selectedFeedback?.feedback_id === fb.feedback_id
                      ? 'border-orange-500 bg-orange-50'
                      : 'hover:border-orange-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(fb.status)}
                      <span className="font-semibold text-slate-900">
                        {fb.name || 'Anonymous'}
                      </span>
                    </div>
                    {getCategoryBadge(fb.category)}
                  </div>
                  <p className="text-sm text-slate-600 mb-2">
                    <Mail className="h-3 w-3 inline mr-1" />
                    {fb.email}
                  </p>
                  {fb.subject && (
                    <p className="font-medium text-slate-900 mb-2">{fb.subject}</p>
                  )}
                  <p className="text-sm text-slate-600 line-clamp-2">{fb.message}</p>
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date(fb.submitted_at).toLocaleString()}
                  </p>
                </div>
              ))
            )}
          </div>

          {/* Feedback Detail Panel */}
          <div className="lg:sticky lg:top-24 h-fit">
            {selectedFeedback ? (
              <div className="card">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-900">Feedback Details</h3>
                  <button
                    onClick={() => deleteFeedback(selectedFeedback.feedback_id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-slate-500">Name</label>
                    <p className="text-slate-900">{selectedFeedback.name || 'Anonymous'}</p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-500">Email</label>
                    <p className="text-slate-900">{selectedFeedback.email}</p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-500">Category</label>
                    <div className="mt-1">{getCategoryBadge(selectedFeedback.category)}</div>
                  </div>

                  {selectedFeedback.subject && (
                    <div>
                      <label className="text-xs font-medium text-slate-500">Subject</label>
                      <p className="text-slate-900">{selectedFeedback.subject}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-medium text-slate-500">Message</label>
                    <p className="text-slate-900 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">
                      {selectedFeedback.message}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-500">Submitted</label>
                    <p className="text-slate-900">
                      {new Date(selectedFeedback.submitted_at).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-2 block">Status</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => updateFeedbackStatus(selectedFeedback.feedback_id, 'pending')}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedFeedback.status === 'pending'
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Pending
                      </button>
                      <button
                        onClick={() => updateFeedbackStatus(selectedFeedback.feedback_id, 'reviewed')}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedFeedback.status === 'reviewed'
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Reviewed
                      </button>
                      <button
                        onClick={() => updateFeedbackStatus(selectedFeedback.feedback_id, 'resolved')}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedFeedback.status === 'resolved'
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Resolved
                      </button>
                    </div>
                  </div>

                  {selectedFeedback.admin_notes && (
                    <div>
                      <label className="text-xs font-medium text-slate-500">Admin Notes</label>
                      <p className="text-slate-900 bg-yellow-50 p-3 rounded-lg">
                        {selectedFeedback.admin_notes}
                      </p>
                    </div>
                  )}

                  {/* Replies Section */}
                  {selectedFeedback.replies && selectedFeedback.replies.length > 0 && (
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-2 block">Reply History</label>
                      <div className="space-y-3">
                        {selectedFeedback.replies.map((reply) => (
                          <div key={reply.reply_id} className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <User className="h-4 w-4 text-blue-500" />
                              <span className="text-sm font-medium text-blue-900">{reply.admin_name}</span>
                              <span className="text-xs text-blue-600">
                                {new Date(reply.replied_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-slate-900 whitespace-pre-wrap">{reply.reply_text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reply Form */}
                  <div className="border-t border-slate-200 pt-4">
                    <label className="text-xs font-medium text-slate-500 mb-2 block">
                      Send Reply to {selectedFeedback.name || 'User'} ({selectedFeedback.email})
                    </label>
                    <form onSubmit={sendReply} className="space-y-3">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Type your reply here... User will be notified via email."
                        className="input min-h-[100px] text-sm"
                        disabled={sendingReply}
                      />
                      <button
                        type="submit"
                        disabled={sendingReply || !replyText.trim()}
                        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sendingReply ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4" />
                            Send Reply & Notify User
                          </>
                        )}
                      </button>
                    </form>
                    <p className="text-xs text-slate-500 mt-2">
                      📧 An email notification will be sent to the user's email address
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card text-center py-12">
                <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Select a feedback to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
